import React, { useState, useEffect } from 'react';
import { TrustedAdvisorService } from '../../lib/trustedAdvisorService';
import { UnlockRequestSummary, UnlockRequestFilters, UnlockRequestStatus } from '../../types/trustedAdvisor';
import { Card } from '../common/Card';
import { Badge } from '../common/Badge';
import { Button } from '../common/Button';
import { Select } from '../common/Select';
import { LoadingSpinner } from '../common/LoadingSpinner';
import { 
  Unlock, 
  Clock, 
  CheckCircle, 
  X, 
  AlertTriangle,
  RefreshCw,
  Eye,
  Shield,
  Calendar
} from 'lucide-react';
import { formatDistanceToNow, format } from 'date-fns';
import { UNLOCK_REQUEST_STATUS_LABELS, DOCUMENT_TYPE_LABELS } from '../../types/trustedAdvisor';

interface UnlockRequestsProps {
  onRefresh: () => void;
}

const UnlockRequests: React.FC<UnlockRequestsProps> = ({ onRefresh }) => {
  const [requests, setRequests] = useState<UnlockRequestSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<UnlockRequestFilters>({});
  const [selectedRequest, setSelectedRequest] = useState<UnlockRequestSummary | null>(null);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    loadRequests();
  }, [filters]);

  const loadRequests = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await TrustedAdvisorService.getUnlockRequests(filters);
      setRequests(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load unlock requests');
    } finally {
      setLoading(false);
    }
  };

  const handleBlockRequest = async (requestId: string) => {
    if (!confirm('Are you sure you want to block this unlock request? This will prevent the advisor from accessing the document.')) {
      return;
    }

    try {
      await TrustedAdvisorService.blockUnlockRequest({ unlock_request_id: requestId });
      await loadRequests();
      onRefresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to block unlock request');
    }
  };

  const getStatusColor = (status: UnlockRequestStatus) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'approved':
        return 'bg-green-100 text-green-800';
      case 'blocked':
        return 'bg-red-100 text-red-800';
      case 'unlocked':
        return 'bg-blue-100 text-blue-800';
      case 'expired':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status: UnlockRequestStatus) => {
    switch (status) {
      case 'pending':
        return <Clock className="w-4 h-4" />;
      case 'approved':
        return <CheckCircle className="w-4 h-4" />;
      case 'blocked':
        return <X className="w-4 h-4" />;
      case 'unlocked':
        return <Unlock className="w-4 h-4" />;
      case 'expired':
        return <AlertTriangle className="w-4 h-4" />;
      default:
        return <Unlock className="w-4 h-4" />;
    }
  };

  const getTimeRemaining = (requestedAt: string, waitPeriodDays: number) => {
    const requestedDate = new Date(requestedAt);
    const unlockDate = new Date(requestedDate.getTime() + (waitPeriodDays * 24 * 60 * 60 * 1000));
    const now = new Date();
    
    if (now >= unlockDate) {
      return 'Ready to unlock';
    }
    
    const diff = unlockDate.getTime() - now.getTime();
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    
    if (days > 0) {
      return `${days} days, ${hours} hours remaining`;
    } else {
      return `${hours} hours remaining`;
    }
  };

  const filteredRequests = requests.filter(request => {
    if (filters.status && request.status !== filters.status) return false;
    if (filters.advisor_id && request.advisor_id !== filters.advisor_id) return false;
    if (filters.document_type && request.document_type !== filters.document_type) return false;
    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Unlock Requests</h2>
          <p className="text-gray-600 mt-1">
            Manage post-death document access requests from trusted advisors
          </p>
        </div>
        <Button onClick={loadRequests} variant="outline" className="flex items-center">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Error Display */}
      {error && (
        <Card className="p-4 bg-red-50 border-red-200">
          <p className="text-red-700">{error}</p>
        </Card>
      )}

      {/* Filters */}
      <Card className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Status
            </label>
            <Select
              value={filters.status || 'all'}
              onChange={(value) => setFilters(prev => ({ 
                ...prev, 
                status: value === 'all' ? undefined : value as UnlockRequestStatus 
              }))}
              options={[
                { value: 'all', label: 'All Statuses' },
                ...Object.entries(UNLOCK_REQUEST_STATUS_LABELS).map(([value, label]) => ({ value, label }))
              ]}
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Document Type
            </label>
            <Select
              value={filters.document_type || 'all'}
              onChange={(value) => setFilters(prev => ({ 
                ...prev, 
                document_type: value === 'all' ? undefined : value as any 
              }))}
              options={[
                { value: 'all', label: 'All Document Types' },
                ...Object.entries(DOCUMENT_TYPE_LABELS).map(([value, label]) => ({ value, label }))
              ]}
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Date Range
            </label>
            <div className="grid grid-cols-2 gap-2">
              <input
                type="date"
                value={filters.date_from || ''}
                onChange={(e) => setFilters(prev => ({ ...prev, date_from: e.target.value }))}
                className="px-3 py-2 border border-gray-300 rounded-md text-sm"
              />
              <input
                type="date"
                value={filters.date_to || ''}
                onChange={(e) => setFilters(prev => ({ ...prev, date_to: e.target.value }))}
                className="px-3 py-2 border border-gray-300 rounded-md text-sm"
              />
            </div>
          </div>
        </div>
      </Card>

      {/* Requests List */}
      <div className="space-y-4">
        {filteredRequests.length === 0 ? (
          <Card className="p-12 text-center">
            <Unlock className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No unlock requests found
            </h3>
            <p className="text-gray-500">
              {filters.status || filters.document_type || filters.date_from || filters.date_to
                ? 'Try adjusting your filters to see more results.'
                : 'Unlock requests will appear here when trusted advisors request access to documents after death.'
              }
            </p>
          </Card>
        ) : (
          filteredRequests.map((request) => (
            <Card key={request.id} className="p-6 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-4">
                  {/* Status Icon */}
                  <div className={`p-3 rounded-lg ${getStatusColor(request.status)}`}>
                    {getStatusIcon(request.status)}
                  </div>

                  {/* Request Details */}
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      <h3 className="text-lg font-semibold text-gray-900">
                        {request.first_name} {request.last_name}
                      </h3>
                      <Badge className={getStatusColor(request.status)}>
                        {UNLOCK_REQUEST_STATUS_LABELS[request.status]}
                      </Badge>
                    </div>
                    
                    <p className="text-sm text-gray-600 mb-2">
                      Requested access to <strong>{DOCUMENT_TYPE_LABELS[request.document_type]}</strong>
                    </p>
                    
                    <div className="flex items-center space-x-4 text-sm text-gray-500">
                      <div className="flex items-center">
                        <Calendar className="w-4 h-4 mr-1" />
                        <span>Requested {formatDistanceToNow(new Date(request.requested_at), { addSuffix: true })}</span>
                      </div>
                      
                      {request.status === 'pending' && (
                        <div className="flex items-center">
                          <Clock className="w-4 h-4 mr-1" />
                          <span>{getTimeRemaining(request.requested_at, request.unlock_wait_period_days)}</span>
                        </div>
                      )}
                      
                      {request.unlocked_at && (
                        <div className="flex items-center">
                          <Unlock className="w-4 h-4 mr-1" />
                          <span>Unlocked {formatDistanceToNow(new Date(request.unlocked_at), { addSuffix: true })}</span>
                        </div>
                      )}
                    </div>

                    {/* Additional Details */}
                    <div className="mt-3 p-3 bg-gray-50 rounded-md">
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="font-medium text-gray-700">Wait Period:</span>
                          <span className="ml-2 text-gray-600">{request.unlock_wait_period_days} days</span>
                        </div>
                        <div>
                          <span className="font-medium text-gray-700">Unlocker:</span>
                          <span className="ml-2 text-gray-600">
                            {request.unlocker_id ? 'Assigned' : 'Not assigned'}
                          </span>
                        </div>
                        {request.unlock_expires_at && (
                          <div className="col-span-2">
                            <span className="font-medium text-gray-700">Access Expires:</span>
                            <span className="ml-2 text-gray-600">
                              {format(new Date(request.unlock_expires_at), 'PPP')}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setSelectedRequest(request);
                      setShowDetails(true);
                    }}
                    className="flex items-center"
                  >
                    <Eye className="w-4 h-4 mr-1" />
                    Details
                  </Button>
                  
                  {request.status === 'pending' && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleBlockRequest(request.id)}
                      className="flex items-center text-red-600 hover:text-red-700"
                    >
                      <X className="w-4 h-4 mr-1" />
                      Block
                    </Button>
                  )}
                </div>
              </div>
            </Card>
          ))
        )}
      </div>

      {/* Request Details Modal */}
      {selectedRequest && showDetails && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Unlock Request Details</h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setShowDetails(false);
                  setSelectedRequest(null);
                }}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
            
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Advisor</label>
                  <p className="text-sm text-gray-900">{selectedRequest.first_name} {selectedRequest.last_name}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Email</label>
                  <p className="text-sm text-gray-900">{selectedRequest.email}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Document Type</label>
                  <p className="text-sm text-gray-900">{DOCUMENT_TYPE_LABELS[selectedRequest.document_type]}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Status</label>
                  <Badge className={getStatusColor(selectedRequest.status)}>
                    {UNLOCK_REQUEST_STATUS_LABELS[selectedRequest.status]}
                  </Badge>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Requested At</label>
                  <p className="text-sm text-gray-900">
                    {format(new Date(selectedRequest.requested_at), 'PPP p')}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Wait Period</label>
                  <p className="text-sm text-gray-900">{selectedRequest.unlock_wait_period_days} days</p>
                </div>
              </div>
              
              {selectedRequest.unlocked_at && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">Unlocked At</label>
                  <p className="text-sm text-gray-900">
                    {format(new Date(selectedRequest.unlocked_at), 'PPP p')}
                  </p>
                </div>
              )}
              
              {selectedRequest.unlock_expires_at && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">Access Expires</label>
                  <p className="text-sm text-gray-900">
                    {format(new Date(selectedRequest.unlock_expires_at), 'PPP p')}
                  </p>
                </div>
              )}
              
              {selectedRequest.blocked_by_user && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                  <div className="flex items-center">
                    <X className="w-4 h-4 text-red-500 mr-2" />
                    <span className="text-sm text-red-700">This request was blocked by you</span>
                  </div>
                </div>
              )}
            </div>
            
            <div className="flex items-center justify-end space-x-3 mt-6 pt-4 border-t border-gray-200">
              <Button
                variant="outline"
                onClick={() => {
                  setShowDetails(false);
                  setSelectedRequest(null);
                }}
              >
                Close
              </Button>
              
              {selectedRequest.status === 'pending' && (
                <Button
                  variant="outline"
                  onClick={() => {
                    handleBlockRequest(selectedRequest.id);
                    setShowDetails(false);
                    setSelectedRequest(null);
                  }}
                  className="text-red-600 hover:text-red-700"
                >
                  Block Request
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UnlockRequests; 